<template>
  <main class="app-main prices-offers-page" dir="rtl">
    <div class="app-content-header">
      <div class="container-fluid">
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <h3 class="mb-1">عروض الأسعار العقارية</h3>
            <ol class="breadcrumb mb-0">
              <li class="breadcrumb-item"><router-link to="/">الرئيسية</router-link></li>
              <li class="breadcrumb-item active" aria-current="page">عروض الأسعار</li>
            </ol>
          </div>
          <router-link v-if="canCreate" to="/pricesOffers/create" class="btn btn-primary">
            <i class="bi bi-plus-circle ms-1" aria-hidden="true"></i> إضافة عرض سعر
          </router-link>
        </div>
      </div>
    </div>

    <div class="app-content">
      <div class="container-fluid">
        <errorAlert v-if="hasError" :alertMessage="message" :errorNumber="errorNumber" />

        <section class="summary-grid mb-4" aria-label="ملخص عروض الأسعار">
          <div class="summary-card">
            <span class="summary-icon"><i class="bi bi-file-earmark-text" aria-hidden="true"></i></span>
            <div><span class="summary-label">إجمالي عروض الأسعار</span><strong>{{ formatInteger(stats.total) }}</strong><small>جميع العروض الصادرة</small></div>
          </div>
          <div class="summary-card">
            <span class="summary-icon"><i class="bi bi-clock-history" aria-hidden="true"></i></span>
            <div><span class="summary-label">العروض النشطة السارية</span><strong>{{ formatInteger(stats.active) }}</strong><small>جديد وقيد المتابعة</small></div>
          </div>
          <div class="summary-card">
            <span class="summary-icon"><i class="bi bi-file-earmark-check" aria-hidden="true"></i></span>
            <div><span class="summary-label">المقبولة / المعتمدة</span><strong>{{ formatInteger(stats.accepted) }}</strong><small>محولة إلى عقود رسمية</small></div>
          </div>
          <div class="summary-card">
            <span class="summary-icon"><i class="bi bi-ban" aria-hidden="true"></i></span>
            <div><span class="summary-label">المنتهية / الملغاة</span><strong>{{ formatInteger(stats.ended) }}</strong><small>انتهت فترتها أو ألغيت</small></div>
          </div>
          <div class="summary-card total-card">
            <span class="summary-icon"><i class="bi bi-cash-coin" aria-hidden="true"></i></span>
            <div><span class="summary-label">إجمالي المبالغ المطلوبة</span><strong>{{ formatMoney(stats.totalAmount) }}</strong><small>شامل الضريبة والرسوم</small></div>
          </div>
        </section>

        <section class="content-card filter-card mb-4" aria-labelledby="offer-filter-title">
          <div class="content-card-header">
            <h4 id="offer-filter-title" class="mb-0"><i class="bi bi-funnel-fill ms-2 text-primary" aria-hidden="true"></i>البحث والفلترة المتقدمة</h4>
            <button type="button" class="btn btn-link btn-sm text-decoration-none" @click="clearFilters">
              <i class="bi bi-arrow-clockwise ms-1" aria-hidden="true"></i>إعادة ضبط الفلاتر
            </button>
          </div>
          <form class="content-card-body" @submit.prevent="applyFilters">
            <div class="row g-3">
              <div class="col-12 col-sm-6 col-xl-3">
                <label class="form-label" for="offer-search">البحث (اسم / جوال / عقار / صك / رقم العرض)</label>
                <input id="offer-search" v-model.trim="filters.search" class="form-control" type="search" placeholder="أدخل كلمة البحث..." />
              </div>
              <div class="col-12 col-sm-6 col-xl-3">
                <label class="form-label" for="offer-branch">الفرع</label>
                <select id="offer-branch" v-model="filters.branchId" class="form-select">
                  <option value="">جميع الفروع</option>
                  <option v-for="branch in branches" :key="branch.id" :value="String(branch.id)">{{ branch.name }}</option>
                </select>
              </div>
              <div class="col-12 col-sm-6 col-xl-3">
                <label class="form-label" for="offer-status">حالة العرض</label>
                <select id="offer-status" v-model="filters.status" class="form-select">
                  <option value="">جميع الحالات</option>
                  <option v-for="status in statuses" :key="status.value" :value="status.value">{{ status.label }}</option>
                </select>
              </div>
              <div class="col-12 col-sm-6 col-xl-3">
                <label class="form-label" for="offer-type">الغرض من العرض</label>
                <select id="offer-type" v-model="filters.type" class="form-select">
                  <option value="">جميع الأغراض</option>
                  <option value="sale">بيع</option>
                  <option value="rent">تأجير</option>
                </select>
              </div>
              <div class="col-12 col-sm-6 col-xl-3">
                <label class="form-label" for="offer-date-from">تاريخ الإضافة من</label>
                <input id="offer-date-from" v-model="filters.dateFrom" class="form-control" type="date" :max="filters.dateTo || undefined" />
              </div>
              <div class="col-12 col-sm-6 col-xl-3">
                <label class="form-label" for="offer-date-to">تاريخ الإضافة إلى</label>
                <input id="offer-date-to" v-model="filters.dateTo" class="form-control" type="date" :min="filters.dateFrom || undefined" />
              </div>
              <div class="col-12 col-sm-6 col-xl-3">
                <label class="form-label" for="offer-amount-from">المبلغ من (ر.س)</label>
                <input id="offer-amount-from" v-model="filters.amountFrom" class="form-control" type="number" min="0" step="0.01" placeholder="أقل مبلغ" />
              </div>
              <div class="col-12 col-sm-6 col-xl-3">
                <label class="form-label" for="offer-amount-to">المبلغ إلى (ر.س)</label>
                <input id="offer-amount-to" v-model="filters.amountTo" class="form-control" type="number" step="0.01" :min="filters.amountFrom || 0" placeholder="أعلى مبلغ" />
              </div>
            </div>
            <div class="text-start mt-3">
              <button type="submit" class="btn btn-primary" :disabled="loading">
                <i class="bi bi-search ms-1" aria-hidden="true"></i> بحث
              </button>
            </div>
          </form>
        </section>

        <section class="content-card results-card mb-4" aria-labelledby="offers-table-title">
          <div class="content-card-header">
            <h4 id="offers-table-title" class="mb-0"><i class="bi bi-list-check ms-2" aria-hidden="true"></i>جدول عروض الأسعار المسجلة</h4>
            <span class="result-count">{{ formatInteger(total) }} عرض</span>
          </div>
          <div v-if="loading" class="p-4"><mainLoader /></div>
          <div v-else-if="!rows.length" class="empty-state">
            <i class="bi bi-file-earmark-search" aria-hidden="true"></i>
            <p class="mb-0">لا توجد عروض أسعار تطابق البحث.</p>
          </div>
          <div v-else class="table-responsive">
            <table class="table offer-table align-middle mb-0">
              <thead>
                <tr>
                  <th scope="col">رقم العرض</th>
                  <th scope="col">التاريخ والانتهاء</th>
                  <th scope="col">اسم العميل والجوال</th>
                  <th scope="col">العقار / الوحدة</th>
                  <th scope="col">الغرض والفرع</th>
                  <th scope="col">السعر الأساسي</th>
                  <th scope="col">المطلوب (شامل الضريبة)</th>
                  <th scope="col">الدفعات المالية</th>
                  <th scope="col">الحالة</th>
                  <th scope="col">الإجراءات والعمليات</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="offer in rows" :key="offer.id">
                  <td class="fw-semibold offer-number" dir="rtl">{{ offer.offerNumber || offer.number }}</td>
                  <td>
                    <span class="d-block fw-semibold" dir="rtl">{{ dateOnly(offer.offerDate || offer.createdAt) }}</span>
                    <small class="text-muted">حتى {{ dateOnly(offer.expiryDate) }}</small>
                  </td>
                  <td>
                    <span class="d-block fw-semibold">{{ offer.clientName }}</span>
                    <small class="text-muted" dir="rtl">{{ offer.clientMobile || '—' }}</small>
                  </td>
                  <td>
                    <span class="d-block fw-semibold">{{ offer.propertyName || '—' }}</span>
                    <small class="text-muted">{{ offer.unitNumber ? `وحدة ${offer.unitNumber}` : '—' }}<span v-if="offer.areaSqm"> · {{ formatInteger(offer.areaSqm) }} م²</span></small>
                    <small v-if="offer.deedNumber" class="d-block text-muted">رقم الصك: <bdi>{{ offer.deedNumber }}</bdi></small>
                  </td>
                  <td>
                    <span class="type-badge">{{ typeLabel(offer.type) }}</span>
                    <small class="d-block text-muted mt-1">{{ offer.branchName || '—' }}</small>
                  </td>
                  <td class="amount-cell">
                    <span :class="{ 'text-decoration-line-through text-muted': Number(offer.discountAmount) > 0 }">{{ formatMoney(offer.basePrice) }}</span>
                    <small v-if="Number(offer.discountAmount) > 0" class="d-block discounted-price">بعد الخصم: {{ formatMoney(offer.discountedBasePrice) }}</small>
                  </td>
                  <td class="amount-cell fw-semibold">{{ formatMoney(offer.totalAmount) }}</td>
                  <td class="installments-cell">
                    <template v-if="offer.installments.length">
                      <span class="installments-count">{{ formatInteger(offer.installments.length) }} دفعة</span>
                      <small class="d-block text-muted mt-1">المجدول: {{ formatMoney(installmentsTotal(offer.installments)) }}</small>
                      <details class="installments-details mt-1">
                        <summary>عرض تفاصيل الدفعات</summary>
                        <ol class="installments-items">
                          <li v-for="(installment, index) in offer.installments" :key="index">
                            <div class="installment-line">
                              <strong>{{ installment.label || `الدفعة ${index + 1}` }}</strong>
                              <span dir="ltr">{{ formatMoney(installment.amount) }}</span>
                            </div>
                            <small class="installment-meta">{{ formatPercentage(installment.percentage) }}%<span v-if="installment.dueCondition"> · {{ installment.dueCondition }}</span></small>
                          </li>
                        </ol>
                      </details>
                    </template>
                    <span v-else class="text-muted">لا توجد دفعات</span>
                  </td>
                  <td>
                    <select v-if="canUpdate" :value="offer.status" class="form-select form-select-sm status-select"
                      :class="statusClass(offer.status)" :aria-label="`حالة العرض ${offer.offerNumber || offer.number}`"
                      :disabled="changingStatusId === offer.id" @change="changeStatus(offer, $event)">
                      <option v-for="status in statuses" :key="status.value" :value="status.value">{{ status.label }}</option>
                    </select>
                    <span v-else class="status-label" :class="statusClass(offer.status)">{{ statusLabel(offer.status) }}</span>
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-1 actions">
                      <router-link :to="`/pricesOffers/print/${offer.id}`" class="action-button print-action" :aria-label="`طباعة العرض ${offer.offerNumber || offer.number}`" title="طباعة / PDF" target="_blank">
                        <i class="bi bi-printer" aria-hidden="true"></i>
                      </router-link>
                      <router-link v-if="canUpdate" :to="`/pricesOffers/update/${offer.id}`" class="action-button edit-action" :aria-label="`تعديل العرض ${offer.offerNumber || offer.number}`" title="تعديل">
                        <i class="bi bi-pencil-square" aria-hidden="true"></i>
                      </router-link>
                      <button v-if="canDelete" type="button" class="action-button delete-action" :aria-label="`حذف العرض ${offer.offerNumber || offer.number}`" title="حذف"
                        :disabled="deletingId === offer.id" @click="deleteOffer(offer)">
                        <i class="bi bi-trash" aria-hidden="true"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="!loading && pages.length > 1" class="pagination-bar">
            <span class="text-muted small">صفحة {{ page }} من {{ totalPages }}</span>
            <nav aria-label="صفحات عروض الأسعار">
              <ul class="pagination pagination-sm mb-0">
                <li class="page-item" :class="{ disabled: page <= 1 }">
                  <button type="button" class="page-link" :disabled="page <= 1" @click="goToPage(page - 1)">السابق</button>
                </li>
                <li v-for="pageNumber in pages" :key="pageNumber" class="page-item" :class="{ active: pageNumber === page }">
                  <button type="button" class="page-link" :aria-current="pageNumber === page ? 'page' : undefined" @click="goToPage(pageNumber)">{{ pageNumber }}</button>
                </li>
                <li class="page-item" :class="{ disabled: page >= totalPages }">
                  <button type="button" class="page-link" :disabled="page >= totalPages" @click="goToPage(page + 1)">التالي</button>
                </li>
              </ul>
            </nav>
          </div>
        </section>
      </div>
    </div>
  </main>
</template>

<script>
import { checkAccess, toast } from '@/helpers/tools';

const statuses = [
  { value: 'new', label: 'جديد' },
  { value: 'follow_up', label: 'قيد المتابعة' },
  { value: 'accepted', label: 'مقبول' },
  { value: 'converted', label: 'محوّل لعقد' },
  { value: 'expired', label: 'منتهي' },
  { value: 'cancelled', label: 'ملغي' },
];

const emptyFilters = () => ({
  search: '', branchId: '', status: '', type: '', dateFrom: '', dateTo: '', amountFrom: '', amountTo: '',
});

function queryValue(value) {
  return String(Array.isArray(value) ? value[0] || '' : value || '');
}

function normalizeInstallments(value) {
  let rows = value;
  if (typeof rows === 'string') {
    try { rows = JSON.parse(rows); } catch (_) { return []; }
  }
  return Array.isArray(rows)
    ? rows.filter(row => row && typeof row === 'object' && !Array.isArray(row))
    : [];
}

export default {
  name: 'PricesOffersList',
  data() {
    const filters = emptyFilters();
    Object.keys(filters).forEach(key => { filters[key] = queryValue(this.$route.query[key]); });
    return {
      statuses,
      filters,
      page: Math.max(1, parseInt(this.$route.query.page, 10) || 1),
      rows: [],
      branches: [],
      pages: [],
      total: 0,
      stats: { total: 0, active: 0, accepted: 0, ended: 0, totalAmount: 0 },
      loading: true,
      hasError: false,
      message: '',
      errorNumber: 0,
      changingStatusId: null,
      deletingId: null,
      requestSerial: 0,
    };
  },
  computed: {
    canCreate() { return checkAccess('prices_offers_create'); },
    canUpdate() { return checkAccess('prices_offers_update'); },
    canDelete() { return checkAccess('prices_offers_delete'); },
    totalPages() { return this.pages.length ? Math.max(...this.pages) : 1; },
  },
  methods: {
    formatInteger(value) {
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(Number(value) || 0);
    },
    formatMoney(value) {
      return `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0)} ر.س`;
    },
    formatPercentage(value) {
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(Number(value) || 0);
    },
    installmentsTotal(installments) {
      const cents = installments.reduce((sum, installment) => {
        const amount = Number(installment.amount);
        return sum + (Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0);
      }, 0);
      return cents / 100;
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : '—';
    },
    typeLabel(type) {
      return type === 'sale' ? 'بيع' : type === 'rent' ? 'تأجير' : '—';
    },
    statusLabel(status) {
      return statuses.find(item => item.value === status)?.label || status || '—';
    },
    statusClass(status) {
      return `status-${status || 'unknown'}`;
    },
    params() {
      const params = { page: this.page, limit: 20 };
      for (const [key, value] of Object.entries(this.filters)) {
        if (value !== '' && value !== null && value !== undefined) params[key] = value;
      }
      return params;
    },
    syncUrl(params) {
      this.$router.replace({ query: params }).catch(() => {});
    },
    async loadList() {
      const request = ++this.requestSerial;
      this.loading = true;
      this.hasError = false;
      const params = this.params();
      this.syncUrl(params);
      try {
        const { data } = await this.$http.get('/api/admin/pricesOffers/list', { params });
        if (request !== this.requestSerial) return;
        this.rows = Array.isArray(data.rows)
          ? data.rows.map(row => ({ ...row, installments: normalizeInstallments(row.installments) }))
          : [];
        this.stats = { ...this.stats, ...(data.stats || {}) };
        this.total = Number(data.total) || 0;
        this.pages = Array.isArray(data.pages) ? data.pages.map(Number).filter(Number.isFinite) : [];
        this.branches = Array.isArray(data.filterOptions?.branches) ? data.filterOptions.branches : [];
      } catch (error) {
        if (request !== this.requestSerial) return;
        [this.message, this.errorNumber] = this.$globalError(error, true);
        this.hasError = true;
      } finally {
        if (request === this.requestSerial) this.loading = false;
      }
    },
    applyFilters() {
      this.page = 1;
      this.loadList();
    },
    clearFilters() {
      this.filters = emptyFilters();
      this.page = 1;
      this.loadList();
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.page) return;
      this.page = page;
      this.loadList();
    },
    async changeStatus(offer, event) {
      const status = event.target.value;
      if (!this.canUpdate || status === offer.status) return;
      this.changingStatusId = offer.id;
      try {
        await this.$http.patch(`/api/admin/pricesOffers/status/${offer.id}`, { status });
        toast('تم تحديث حالة العرض', 'success');
        await this.loadList();
      } catch (error) {
        event.target.value = offer.status;
        toast(this.$globalError(error));
      } finally {
        this.changingStatusId = null;
      }
    },
    async deleteOffer(offer) {
      if (!this.canDelete || this.deletingId !== null) return;
      const confirmation = await this.$swal.fire({
        title: 'حذف عرض السعر؟',
        text: 'لا يمكن استعادة العرض بعد حذفه.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'حذف',
        cancelButtonText: 'إلغاء',
        confirmButtonColor: '#dc3545',
      });
      if (!confirmation.isConfirmed) return;
      this.deletingId = offer.id;
      try {
        await this.$http.delete(`/api/admin/pricesOffers/delete/${offer.id}`);
        if (this.rows.length === 1 && this.page > 1) this.page -= 1;
        toast('تم حذف عرض السعر', 'success');
        await this.loadList();
      } catch (error) {
        toast(this.$globalError(error));
      } finally {
        this.deletingId = null;
      }
    },
  },
  created() { this.loadList(); },
};
</script>

<style scoped>
.prices-offers-page {
  --offer-text: #26351f;
  --offer-muted: #697663;
  --offer-surface: #fff;
  --offer-border: rgba(62, 87, 46, .15);
  --offer-divider: #e1e8da;
  --offer-tint: #f4f7f0;
  --offer-soft: #ebf4df;
  --offer-hover: #f7faf4;
  --offer-accent: var(--brand-green);
  --offer-gold: var(--brand-gold-light);
  color: var(--offer-text);
}
.summary-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 1rem; }
.summary-card { min-height: 112px; display: flex; align-items: center; justify-content: space-between; gap: .8rem; padding: 1rem; background: var(--offer-surface); border: 1px solid var(--offer-border); border-radius: 16px; box-shadow: 0 3px 12px rgba(28, 25, 26, .045); }
.summary-card > div { min-width: 0; display: flex; flex-direction: column; }
.summary-icon { order: 2; flex: none; display: grid; place-items: center; width: 48px; height: 48px; border-radius: 12px; color: var(--offer-accent); background: var(--offer-soft); font-size: 1.45rem; }
.summary-label { color: var(--offer-muted); font-size: .84rem; }
.summary-card strong { color: var(--offer-accent); font-size: 1.35rem; line-height: 1.6; }
.summary-card small { color: var(--offer-muted); font-size: .76rem; }
.total-card { background: var(--brand-green-dark); border-color: var(--brand-green-dark); }
.total-card .summary-icon { color: var(--offer-gold); background: rgba(216, 202, 159, .16); }
.total-card .summary-label, .total-card small { color: #e5ebdc; }
.total-card strong { color: var(--offer-gold); }
.content-card { background: var(--offer-surface); border: 1px solid var(--offer-border); border-radius: 17px; box-shadow: 0 3px 12px rgba(28, 25, 26, .045); overflow: hidden; }
.content-card-header { min-height: 55px; padding: .9rem 1.25rem; display: flex; justify-content: space-between; align-items: center; gap: 1rem; border-bottom: 1px solid var(--offer-divider); }
.content-card-header h4 { font-size: 1.04rem; font-weight: 700; color: var(--offer-text); }
.content-card-body { padding: 1.1rem 1.25rem 1.3rem; }
.filter-card .form-label { margin-bottom: .35rem; color: var(--offer-muted); font-size: .82rem; }
.filter-card .form-control, .filter-card .form-select { min-height: 39px; border-color: var(--offer-border); border-radius: 8px; }
.filter-card .btn-link { color: var(--offer-accent); }
.result-count { flex: none; padding: .28rem .7rem; border-radius: 100px; background: var(--offer-soft); color: var(--offer-accent); font-size: .8rem; }
.offer-table { min-width: 1380px; font-size: .82rem; --bs-table-color: var(--offer-text); --bs-table-bg: var(--offer-surface); --bs-table-border-color: var(--offer-divider); }
.offer-table th { padding: .8rem .65rem; border-bottom: 1px solid var(--offer-border); --bs-table-bg: var(--offer-tint); color: var(--offer-text); font-weight: 700; white-space: nowrap; }
.offer-table td { padding: .72rem .65rem; border-color: var(--offer-divider); }
.offer-table tbody tr:hover > * { --bs-table-bg-state: var(--offer-hover); }
.offer-number { color: var(--offer-accent); white-space: nowrap; }
.amount-cell { white-space: nowrap; }
.discounted-price { color: var(--brand-green); font-weight: 700; }
.installments-cell { min-width: 220px; max-width: 280px; }
.installments-count { display: inline-flex; padding: .15rem .5rem; border-radius: 6px; background: var(--offer-soft); color: var(--offer-accent); font-weight: 700; }
.installments-details summary { color: var(--offer-accent); cursor: pointer; font-weight: 600; }
.installments-details summary:focus-visible { outline: 2px solid var(--offer-accent); outline-offset: 2px; }
.installments-items { margin: .5rem 0 0; padding: .5rem; list-style: none; border: 1px solid var(--offer-border); border-radius: 8px; background: var(--offer-tint); }
.installments-items li + li { margin-top: .5rem; padding-top: .5rem; border-top: 1px solid var(--offer-divider); }
.installment-line { display: flex; flex-wrap: wrap; justify-content: space-between; gap: .25rem .5rem; overflow-wrap: anywhere; }
.installment-meta { display: block; margin-top: .15rem; color: var(--offer-muted); white-space: normal; overflow-wrap: anywhere; }
.type-badge { display: inline-block; padding: .12rem .4rem; border-radius: 5px; background: var(--offer-soft); color: var(--offer-accent); }
.status-select { width: 130px; min-height: 34px; font-weight: 700; }
.status-label { display: inline-block; border-radius: 7px; padding: .3rem .55rem; font-weight: 700; white-space: nowrap; }
.status-new { background-color: #ebf4df; color: #3e572e; border-color: #c6d9b7; }
.status-follow_up { background-color: #f5ead0; color: #765d27; border-color: #dfcf9e; }
.status-accepted, .status-converted { background-color: #e5f0df; color: #385e36; border-color: #bfd7b2; }
.status-expired, .status-cancelled { background-color: #f7e8e4; color: #9e493d; border-color: #e6c4bb; }
.actions { white-space: nowrap; }
.action-button { width: 30px; height: 30px; display: inline-grid; place-items: center; border: 0; border-radius: 8px; background: var(--offer-soft); color: var(--offer-accent); text-decoration: none; }
.action-button:hover { filter: brightness(.93); }
.edit-action { background: #f5ead0; color: #765d27; }
.delete-action { background: #f7e8e4; color: #9e493d; }
.action-button:disabled { opacity: .45; cursor: not-allowed; }
.empty-state { padding: 3rem 1rem; text-align: center; color: var(--offer-muted); }
.empty-state i { display: block; margin-bottom: .6rem; font-size: 2rem; }
.pagination-bar { display: flex; justify-content: space-between; align-items: center; gap: 1rem; padding: .8rem 1.25rem; border-top: 1px solid var(--offer-divider); }
.pagination .page-link { color: var(--offer-accent); background: var(--offer-surface); border-color: var(--offer-border); }
.pagination .page-item.active .page-link { color: #fff; background: var(--brand-green); border-color: var(--brand-green); }
.pagination .page-item.disabled .page-link { color: var(--offer-muted); }
@media (max-width: 1250px) { .summary-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 767px) { .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .content-card-header { align-items: flex-start; } }
@media (max-width: 480px) { .summary-grid { grid-template-columns: 1fr; } .content-card-header, .pagination-bar { flex-wrap: wrap; } }
</style>

<style>
html[data-bs-theme="dark"] .prices-offers-page {
  --offer-text: #e4eae0;
  --offer-muted: #a8b2a4;
  --offer-surface: #1a2118;
  --offer-border: rgba(166, 144, 87, .24);
  --offer-divider: rgba(166, 144, 87, .18);
  --offer-tint: #232c20;
  --offer-soft: #2c3828;
  --offer-hover: #293426;
  --offer-accent: #b7cdaa;
  --offer-gold: #d8ca9f;
}
html[data-bs-theme="dark"] .prices-offers-page .breadcrumb-item,
html[data-bs-theme="dark"] .prices-offers-page .breadcrumb-item.active,
html[data-bs-theme="dark"] .prices-offers-page .text-muted { color: var(--offer-muted) !important; }
html[data-bs-theme="dark"] .prices-offers-page .breadcrumb-item a,
html[data-bs-theme="dark"] .prices-offers-page .filter-card .btn-link { color: var(--offer-accent); }
html[data-bs-theme="dark"] .prices-offers-page .summary-card,
html[data-bs-theme="dark"] .prices-offers-page .content-card { box-shadow: 0 8px 24px rgba(0, 0, 0, .18); }
html[data-bs-theme="dark"] .prices-offers-page .summary-card.total-card { background: #263a20; border-color: rgba(216, 202, 159, .28); }
html[data-bs-theme="dark"] .prices-offers-page .filter-card .form-control,
html[data-bs-theme="dark"] .prices-offers-page .filter-card .form-select { color: var(--offer-text); background-color: #121711; border-color: #3b4936; }
html[data-bs-theme="dark"] .prices-offers-page .filter-card .form-control::placeholder { color: #9da99a; }
html[data-bs-theme="dark"] .prices-offers-page .offer-table { --bs-table-color: var(--offer-text); --bs-table-bg: var(--offer-surface); --bs-table-border-color: var(--offer-divider); }
html[data-bs-theme="dark"] .prices-offers-page .offer-table th { --bs-table-bg: var(--offer-tint); color: var(--offer-text); }
html[data-bs-theme="dark"] .prices-offers-page .offer-table tbody tr:hover > * { --bs-table-bg-state: var(--offer-hover); }
html[data-bs-theme="dark"] .prices-offers-page .discounted-price { color: #b9d9a9; }
html[data-bs-theme="dark"] .prices-offers-page .status-new { color: #c5d8b8; background-color: #2a3b25; border-color: #496344; }
html[data-bs-theme="dark"] .prices-offers-page .status-follow_up { color: #e4cd91; background-color: #3a3426; border-color: #74613b; }
html[data-bs-theme="dark"] .prices-offers-page .status-accepted,
html[data-bs-theme="dark"] .prices-offers-page .status-converted { color: #b9d9a9; background-color: #263d29; border-color: #4b6d4f; }
html[data-bs-theme="dark"] .prices-offers-page .status-expired,
html[data-bs-theme="dark"] .prices-offers-page .status-cancelled { color: #f2b1a7; background-color: #442b27; border-color: #79423c; }
html[data-bs-theme="dark"] .prices-offers-page .status-select option { color: var(--offer-text); background: #1a2118; }
html[data-bs-theme="dark"] .prices-offers-page .print-action { color: #c5d8b8; background: #2a3b25; }
html[data-bs-theme="dark"] .prices-offers-page .edit-action { color: #e4cd91; background: #3a3426; }
html[data-bs-theme="dark"] .prices-offers-page .delete-action { color: #f2b1a7; background: #442b27; }
</style>
